import { useState } from 'react';
import EPageHeader from '@/components/everridge/EPageHeader';
import { useTranslation } from 'react-i18next';
import ETableWithApiPagination from '@/components/everridge/table/ETableWithApiPagination';
import NotificationsPageFilter from '@/features/notifications/componentns/NotificationsPageFilter';
import NotificationsPageColumns from '@/features/notifications/componentns/NotificationsPageColumns';
import NotificationsDetails from '@/features/notifications/componentns/NotificationsDetails';
import { useNotificationsListApi } from '@/features/notifications/hooks/useNotificationsListApi';
import { useNotificationsFilter } from '@/features/notifications/hooks/useNotificationsFilter';
import { useUserRole } from '@/features/identity/hooks/useUserRole.ts';
import { Roles } from '@/@types/auth.types.ts';
import { FilterHelper } from '@/utils/filterHelper';
import { Button } from '@/components/ui/button';
import FilterIcon from '@/components/icons/FilterIcon';
import TickDouble_02 from '@/components/icons/TickDouble_02';
import ComposeIcon from '@/components/icons/ComposeIcon';
import NotificationsOwnerList from '@/features/notifications/componentns/NotificationsList/NotificationsOwnerList';
import { useNotificationDetailsApi } from '@/features/notifications/hooks/useNotificationDetailsApi';
import NotificationCreate from '@/features/notifications/componentns/NotificationCreate/NotificationCreate';
import { AppConfig } from '@/config/app.config';
import useMediaQuery from '@/hooks/useMediaQuery';
import NotificationsAdminList from '@/features/notifications/componentns/NotificationsList/NotificationsAdminList';

const NotificationsPage = () => {
	const [notificationId, setNotificationId] = useState('');
	const [showNotificationDetails, setShowNotificationDetails] = useState(false);
	const [showNotificationsFilter, setShowNotificationsFilter] = useState(false);
	const [showNotificationCreate, setShowNotificationCreate] = useState(false);
	const [page, setPage] = useState(0);

	const userType = useUserRole();
	const { adminNotificationsListData, ownerNotificationsListData } =
		useNotificationsListApi({
			role: userType,
			isRead: false,
		});

	const { readAllNotification, isReadingAllNotification } =
		useNotificationDetailsApi();

	const { filter, setFilter } = useNotificationsFilter();

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { t } = useTranslation();
	const columns = NotificationsPageColumns();

	const handleShowNotificationDetails = (id: string) => {
		setNotificationId(id);
		setShowNotificationDetails(true);
	};

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
		setFilter({
			skip: filter.take * newPage,
		});
	};

	const handleCloseNotificationDetails = () => {
		setNotificationId('');
		setShowNotificationDetails(false);
	};

	const handleReadAll = async () => {
		await readAllNotification();
	};

	const filtersQuantity = FilterHelper.countFilterValues(filter, [
		'orderBy',
		'orderDirection',
		'skip',
		'take',
	]);
	
	return (
		<section className={'flex-1 flex flex-col gap-6'}>
			<EPageHeader
				title={t('main.notifications.title')}
				addBackButton={userType === Roles.owner}>
				<div className='relative'>
					{!!filtersQuantity && (
						<div className='absolute -top-1 right-0 size-4 rounded-full bg-error-1000 flex items-center justify-center text-2xs text-popover-10 animate-pulse '>
							{filtersQuantity}
						</div>
					)}
					<Button
						variant='outline'
						size={isMobile ? 'icon' : 'default'}
						onClick={() => setShowNotificationsFilter(true)}>
						<FilterIcon />
						{!isMobile && t('elements.buttons.filter')}
					</Button>
				</div>
				{userType === Roles.admin && (
					<Button
						size={isMobile ? 'icon' : 'default'}
						onClick={() => setShowNotificationCreate(true)}>
						<ComposeIcon className='stroke-popover-10' />
						{!isMobile && t('elements.buttons.compose')}
					</Button>
				)}
				{userType === Roles.owner && !ownerNotificationsListData && (
					<Button
						variant='outline'
						onClick={handleReadAll}
						disabled={isReadingAllNotification}>
						<TickDouble_02 />
						{t('elements.buttons.markAsRead')}
					</Button>
				)}
			</EPageHeader>
			{userType === Roles.admin && !isMobile && (
				<ETableWithApiPagination
					columns={columns}
					data={adminNotificationsListData}
					paginationStep={filter.take}
					page={page}
					setPage={handlePageChange}
					showDetails={handleShowNotificationDetails}
				/>
			)}
			{userType === Roles.admin && isMobile && (
				<NotificationsAdminList
					data={adminNotificationsListData}
					paginationStep={filter.take}
					page={page}
					setPage={handlePageChange}
					showDetails={handleShowNotificationDetails}
				/>
			)}
			{userType === Roles.owner && (
				<NotificationsOwnerList
					showDetails={handleShowNotificationDetails}
				/>
			)}
			{showNotificationDetails && (
				<NotificationsDetails
					id={notificationId}
					role={userType}
					onClose={handleCloseNotificationDetails}
				/>
			)}
			{showNotificationsFilter && (
				<NotificationsPageFilter
					onClose={() => setShowNotificationsFilter(false)}
				/>
			)}
			{showNotificationCreate && (
				<NotificationCreate
					onClose={() => setShowNotificationCreate(false)}
				/>
			)}
		</section>
	);
};

export default NotificationsPage;
